import React, { useState } from 'react';
import { FileDropZone } from './FileDropZone';
import { Button } from '@components/common/Button';
import { Card, CardContent } from '@components/common/Card';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { useUploadDocumentAutoClassify } from '@api/hooks/useUpload';
import { useDocumentWorkflowStore } from '@store/documentWorkflowStore';
import { useNotificationStore } from '@store/notificationStore';
import { X } from 'lucide-react';

interface UnifiedUploadFormProps {
  compact?: boolean;
}

export const UnifiedUploadForm: React.FC<UnifiedUploadFormProps> = ({ compact = false }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const { mutate: uploadDocuments, isPending, data } = useUploadDocumentAutoClassify();
  const addNotification = useNotificationStore((state) => state.addNotification);
  const trackWorkflow = useDocumentWorkflowStore((state) => state.trackWorkflow);

  const handleDrop = (files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      addNotification({ message: 'Please select at least one file', type: 'error' });
      return;
    }

    uploadDocuments(selectedFiles, {
      onSuccess: (response) => {
        setSelectedFiles([]);
        response.documents.forEach((document) => {
          if (document.status === 'submitted' && document.workflow_id) {
            const workflowId = document.workflow_id.endsWith('-extraction')
              ? document.workflow_id
              : `${document.workflow_id}-extraction`;
            trackWorkflow({
              workflowId,
              filename: document.filename,
              documentType: document.document_type,
              correlationId: document.correlation_id,
            });
          }
        });
        const msg =
          response.failed > 0
            ? `${response.submitted} of ${response.total} documents submitted (${response.failed} failed)`
            : `${response.submitted} document${response.submitted !== 1 ? 's' : ''} submitted for processing`;
        addNotification({ message: msg, type: response.failed > 0 ? 'warning' : 'success' });
      },
      onError: (error) => {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        addNotification({ message: `Upload failed: ${errorMessage}`, type: 'error' });
      },
    });
  };

  // Accept all document types
  const acceptedTypes = {
    'text/csv': ['.csv'],
    'application/json': ['.json'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/tiff': ['.tiff', '.tif'],
    'image/gif': ['.gif'],
  };

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      <Card className={compact ? 'p-4' : ''}>
        <CardContent className={compact ? 'space-y-4' : 'space-y-6'}>
          <FileDropZone
            onDrop={handleDrop}
            accept={acceptedTypes}
            maxSize={50 * 1024 * 1024}
            multiple={true}
            disabled={isPending}
            compact={compact}
          />

          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
              </p>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                {selectedFiles.map((file, idx) => (
                  <li
                    key={`${file.name}-${idx}`}
                    className={`flex items-center justify-between text-sm ${compact ? 'px-3 py-2' : 'px-4 py-2'}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-700 dark:text-gray-200">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(idx)}
                      className="ml-3 text-gray-400 hover:text-red-500 transition-colors"
                      disabled={isPending}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isPending && <LoadingSpinner text="Uploading documents..." />}

          {data && (
            <div className={compact ? 'space-y-3' : 'space-y-4'}>
              <div className={`${compact ? 'p-3' : 'p-4'} bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800 space-y-3`}>
                <p className="text-sm font-medium text-green-700 dark:text-green-200">
                  {data.submitted} of {data.total} submitted
                  {data.failed > 0 && (
                    <span className="text-red-600 dark:text-red-400"> ({data.failed} failed)</span>
                  )}
                </p>
              </div>

              <ul className="space-y-2">
                {data.documents.map((doc, idx) => (
                  <li
                    key={idx}
                    className={`${compact ? 'p-2.5' : 'p-3'} rounded-lg border text-sm ${
                      doc.status === 'submitted'
                        ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <span className="font-medium text-gray-700 dark:text-gray-200 truncate">
                        {doc.filename}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          doc.status === 'submitted'
                            ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                            : 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                        }`}
                      >
                        {doc.status}
                      </span>
                    </div>
                    {doc.error && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">{doc.error}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={`flex gap-3 justify-end ${compact ? 'pt-2' : 'pt-4'}`}>
            <Button
              variant="secondary"
              disabled={selectedFiles.length === 0 || isPending}
              onClick={() => setSelectedFiles([])}
            >
              Clear
            </Button>
            <Button
              disabled={selectedFiles.length === 0 || isPending}
              onClick={handleUpload}
            >
              {isPending
                ? 'Uploading...'
                : `Upload ${selectedFiles.length || ''} & Auto-Classify`.trim()}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedUploadForm;
